/**
 * Entities query hooks using generic factory
 */
import type { EntityFilters } from '@/store/entity-filters.store'
import type {
  BaseQueryOptions,
  DetailQueryOptions,
  PaginatedResponse,
} from '@/types/resource.types'
import type { UseQueryResult } from '@tanstack/react-query'
import { useAccountType } from '@/hooks/useAccountType'
import { getErrorStatus } from '@/lib/api/error-utils'
import {
  isRecordQueryInScope,
  scopedRecordKey,
} from '@/lib/realtime/record-query-scope'
import { entityApi } from '@/modules/entities'
import { normalizeEntityLifecycleStageFilter } from '@/store/entity-filters.store'
import { useQuery } from '@tanstack/react-query'

import type { Entity } from '@scrambl/shared'

import { queryKeys } from '../query-keys'
import { useWorkspaceSession } from '../useWorkspaceSession'

const entitiesQueryConfig = {
  queryKeyNamespace: queryKeys.entities.all,
  api: {
    getList: async (filters?: EntityFilters, signal?: AbortSignal) => {
      const requestFilters: EntityFilters & {
        includeCustomFields: boolean
        columnFilters?: Record<string, unknown>
      } = {
        includeCustomFields:
          (filters as { includeCustomFields?: boolean } | undefined)
            ?.includeCustomFields ?? true,
        ...filters,
      }

      const params: Record<string, unknown> = {
        page: requestFilters.page || 1,
        limit: requestFilters.limit || 50,
        sortBy: requestFilters.sortBy,
        sortOrder: requestFilters.sortOrder,
        includeCustomFields: requestFilters.includeCustomFields,
      }

      if (requestFilters.name && requestFilters.name.trim() !== '') {
        params.name = requestFilters.name.trim()
      }

      if (
        requestFilters.entityTypeId &&
        requestFilters.entityTypeId.trim() !== ''
      ) {
        params.entityTypeId = requestFilters.entityTypeId
      }

      // T2 record-state opt-in: unset = server default (active + provisional,
      // merged/archived tombstones hidden); 'all' or an explicit state opts in.
      if (requestFilters.recordState) {
        params.recordState = requestFilters.recordState
      }

      // Merge column filters with the lifecycle quick-filter chip (Path C).
      const mergedColumnFilters: Record<string, unknown> = {
        ...((requestFilters.columnFilters as
          | Record<string, unknown>
          | undefined) ?? {}),
      }
      const lifecycleStageIds = normalizeEntityLifecycleStageFilter(
        requestFilters.lifecycleStage,
      )
      if (lifecycleStageIds.length > 0) {
        mergedColumnFilters.lifecycleStage = lifecycleStageIds
      }
      if (Object.keys(mergedColumnFilters).length > 0) {
        params.columnFilters = JSON.stringify(mergedColumnFilters)
      }

      return entityApi.getAll(params, { signal })
    },
    getDetail: (id: string) => entityApi.getById(id),
  },
  requiresOrganization: true,
}

function readerResult<T>(query: UseQueryResult<T>) {
  const denied = [401, 403, 404].includes(getErrorStatus(query.error) ?? 0)
  return { ...query, data: denied ? undefined : query.data }
}

export function useEntitiesQuery(
  filters?: EntityFilters,
  options?: BaseQueryOptions,
) {
  const scope = useWorkspaceSession()
  // Personal spaces have no clients and the /entities API rejects them, so
  // shared surfaces (pickers, favorites, filters) skip the request.
  const { isPersonal } = useAccountType()
  return readerResult(
    useQuery<PaginatedResponse<Entity>>({
      queryKey: scopedRecordKey(queryKeys.entities.list(filters), scope),
      queryFn: ({ signal }) =>
        scope.run(signal, signal =>
          entitiesQueryConfig.api.getList(filters, signal),
        ),
      enabled: options?.enabled !== false && scope.enabled && !isPersonal,
      placeholderData: (previous, query) =>
        query && isRecordQueryInScope(query.queryKey, scope)
          ? previous
          : undefined,
      staleTime: options?.staleTime ?? 60_000,
      gcTime: options?.gcTime ?? 600_000,
      refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
      refetchOnReconnect: options?.refetchOnReconnect ?? true,
      refetchOnMount: options?.refetchOnMount ?? true,
    }),
  )
}

export function useEntityQuery(
  id: string | undefined,
  options?: DetailQueryOptions,
) {
  const scope = useWorkspaceSession()
  const { isPersonal } = useAccountType()
  return readerResult(
    useQuery<Entity>({
      queryKey: scopedRecordKey(queryKeys.entities.detail(id ?? ''), scope),
      queryFn: ({ signal }) =>
        scope.run(signal, signal => entityApi.getById(id!, { signal })),
      enabled:
        options?.enabled !== false && !!id && scope.enabled && !isPersonal,
      staleTime: options?.staleTime ?? 60_000,
      gcTime: options?.gcTime ?? 600_000,
      refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
      refetchOnReconnect: options?.refetchOnReconnect ?? true,
      refetchOnMount: options?.refetchOnMount ?? true,
    }),
  )
}

/**
 * useEntitiesQuery under a non-hook name for useModuleTable configs: passing a
 * use*-named import as a value makes React Compiler bail out of the consuming
 * component. Same function, only ever called unconditionally inside the hook.
 */
export const entitiesListQuery = useEntitiesQuery
